import React, { memo, useCallback, useEffect, useRef } from 'react';
import { animate } from 'motion/react';
import { cn } from '../../lib/utils';
import { useFinePointer } from '../../hooks/useMediaQuery';

/*
 * A segment of the card border lights up and follows the pointer around the card.
 * Adapted from Aceternity UI "Glowing Effect" via 21st.dev:
 * https://21st.dev/manuarora700/glowing-effect — recoloured to the DVS teal/cyan and
 * enabled only for fine pointers. Place inside a `relative rounded-*` container.
 */
const GRADIENT = `radial-gradient(circle, hsl(var(--brand)) 10%, transparent 20%),
  radial-gradient(circle at 40% 40%, hsl(var(--brand-2)) 5%, transparent 15%),
  radial-gradient(circle at 60% 60%, hsl(var(--brand)) 10%, transparent 20%),
  radial-gradient(circle at 40% 60%, hsl(var(--brand-2)) 10%, transparent 20%),
  repeating-conic-gradient(from 236.84deg at 50% 50%,
    hsl(var(--brand)) 0%,
    hsl(var(--brand-2)) calc(25% / var(--repeating-conic-gradient-times)),
    hsl(var(--brand)) calc(50% / var(--repeating-conic-gradient-times)),
    hsl(var(--brand-2)) calc(75% / var(--repeating-conic-gradient-times)),
    hsl(var(--brand)) calc(100% / var(--repeating-conic-gradient-times)))`;

const GlowingEffect = memo(({ spread = 40, proximity = 64, inactiveZone = 0.01, movementDuration = 2, borderWidth = 1, className }) => {
  const fine = useFinePointer();
  const containerRef = useRef(null);
  const lastPosition = useRef({ x: 0, y: 0 });
  const frame = useRef(0);

  const handleMove = useCallback(
    e => {
      if (!containerRef.current) return;
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        const element = containerRef.current;
        if (!element) return;
        const { left, top, width, height } = element.getBoundingClientRect();
        const mouseX = e?.x ?? lastPosition.current.x;
        const mouseY = e?.y ?? lastPosition.current.y;
        if (e) lastPosition.current = { x: mouseX, y: mouseY };

        const center = [left + width * 0.5, top + height * 0.5];
        if (Math.hypot(mouseX - center[0], mouseY - center[1]) < 0.5 * Math.min(width, height) * inactiveZone) {
          element.style.setProperty('--active', '0');
          return;
        }
        const isActive =
          mouseX > left - proximity && mouseX < left + width + proximity && mouseY > top - proximity && mouseY < top + height + proximity;
        element.style.setProperty('--active', isActive ? '1' : '0');
        if (!isActive) return;

        const currentAngle = parseFloat(element.style.getPropertyValue('--start')) || 0;
        const targetAngle = (180 * Math.atan2(mouseY - center[1], mouseX - center[0])) / Math.PI + 90;
        const angleDiff = ((targetAngle - currentAngle + 180) % 360) - 180;
        animate(currentAngle, currentAngle + angleDiff, {
          duration: movementDuration,
          ease: [0.16, 1, 0.3, 1],
          onUpdate: value => element.style.setProperty('--start', String(value))
        });
      });
    },
    [inactiveZone, proximity, movementDuration]
  );

  useEffect(() => {
    if (!fine) return undefined;
    const onScroll = () => handleMove();
    const onPointerMove = e => handleMove(e);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.body.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      cancelAnimationFrame(frame.current);
      window.removeEventListener('scroll', onScroll);
      document.body.removeEventListener('pointermove', onPointerMove);
    };
  }, [handleMove, fine]);

  if (!fine) return null;

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        '--spread': spread,
        '--start': '0',
        '--active': '0',
        '--glowingeffect-border-width': `${borderWidth}px`,
        '--repeating-conic-gradient-times': '5',
        '--gradient': GRADIENT
      }}
      className={cn('pointer-events-none absolute inset-0 rounded-[inherit]', className)}
    >
      <div
        className={cn(
          'rounded-[inherit]',
          'after:absolute after:inset-[calc(-1*var(--glowingeffect-border-width))] after:rounded-[inherit] after:content-[""]',
          'after:[border:var(--glowingeffect-border-width)_solid_transparent]',
          'after:[background:var(--gradient)] after:[background-attachment:fixed]',
          'after:opacity-[var(--active)] after:transition-opacity after:duration-300',
          'after:[mask-clip:padding-box,border-box] after:[mask-composite:intersect]',
          'after:[mask-image:linear-gradient(#0000,#0000),conic-gradient(from_calc((var(--start)-var(--spread))*1deg),#00000000_0deg,#fff,#00000000_calc(var(--spread)*2deg))]'
        )}
      />
    </div>
  );
});

GlowingEffect.displayName = 'GlowingEffect';

export default GlowingEffect;
